import { z } from 'zod';

/**
 * Contracts for the admin station / fuel-price endpoints.
 *
 * @typedef {'fuel'|'ev'|'hybrid'|'lpg_cng'|'none'} HubType
 * @typedef {'petrol'|'diesel'|'lpg'|'cng'|'ev'} FuelCategory
 * @typedef {{ userId: string, name: string|null }} UpdatedBy
 * @typedef {{ fuelOptionId: number, code: string, category: string, name: string, unit: string,
 *   latestPrice: number|null, isAvailable: boolean, source: string|null, scanCount: number,
 *   updatedAt: string|null, updatedBy: UpdatedBy|null }} StationFuel
 * @typedef {{ placeId: string, name: string|null, address: string|null, city: string|null,
 *   postalCode: string|null, googleStatus: string, hubType: string, fuels: StationFuel[],
 *   latestPrice: { fuelOptionId: number, name: string, category: string, price: number, unit: string, updatedAt: string|null }|null,
 *   lastUpdatedAt: string|null }} Station
 * @typedef {{ id: number, code: string, category: string, name_en: string, name_du: string, unit: string,
 *   sortOrder: number, isActive: boolean }} FuelOption
 * @typedef {{ placeId: string, name: string|null, address: string|null, city: string|null, primaryType: string|null,
 *   isFuelStation: boolean, alreadyConfigured: boolean, configuredFuelCount: number }} GoogleStation
 */

const str = z.string().nullable().optional().transform((v) => v ?? null);
const flag = z.union([z.boolean(), z.literal(0), z.literal(1)]).transform((v) => v === true || v === 1);

const updatedBySchema = z
  .object({ userId: z.string(), name: z.string().nullable().optional() })
  .nullable()
  .optional()
  .transform((v) => (v ? { userId: v.userId, name: v.name ?? null } : null));

export const stationFuelSchema = z.object({
  fuelOptionId: z.number().int(),
  code: z.string(),
  category: z.string(),
  name: z.string(),
  unit: z.string(),
  latestPrice: z.number().nullable().optional().transform((v) => v ?? null),
  isAvailable: flag,
  source: str,
  scanCount: z.number().optional().transform((v) => v ?? 0),
  updatedAt: str,
  updatedBy: updatedBySchema,
});

export const stationSchema = z.object({
  placeId: z.string().min(1),
  name: str,
  address: str,
  city: str,
  postalCode: str,
  googleStatus: z.string().optional().transform((v) => v ?? 'ok'),
  hubType: z.string(),
  fuels: z.array(stationFuelSchema),
  latestPrice: z
    .object({
      fuelOptionId: z.number().int(),
      name: z.string(),
      category: z.string(),
      price: z.number(),
      unit: z.string(),
      updatedAt: str,
    })
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  lastUpdatedAt: str,
});

export const stationListSchema = z.object({
  stations: z.array(z.unknown()),
  pagination: z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    hasNextPage: z.boolean(),
    hasPrevPage: z.boolean(),
  }),
  hubCounts: z.record(z.string(), z.number()),
  googleAvailable: z.boolean().optional().transform((v) => v ?? true),
});

export const fuelOptionSchema = z.object({
  id: z.number().int(),
  code: z.string(),
  category: z.string(),
  name_en: z.string(),
  name_du: z.string().nullable().optional(),
  unit: z.string(),
  sortOrder: z.number().optional().transform((v) => v ?? 0),
  isActive: flag,
});

/** API 4 / save / reset responses: every option with this station's entry (or null = unknown). */
export const stationFuelsSchema = z.object({
  placeId: z.string(),
  fuels: z.array(
    z.object({
      fuelOptionId: z.number().int(),
      code: z.string(),
      category: z.string(),
      name_en: z.string(),
      name_du: z.string().nullable().optional(),
      unit: z.string(),
      entry: z
        .object({
          latestPrice: z.number().nullable().optional().transform((v) => v ?? null),
          isAvailable: flag,
          source: str,
          scanCount: z.number().optional().transform((v) => v ?? 0),
          updatedAt: str,
          updatedBy: updatedBySchema,
        })
        .nullable(),
    }),
  ),
});

export const googleResultSchema = z.object({
  placeId: z.string().min(1),
  name: str,
  address: str,
  city: str,
  postalCode: str,
  primaryType: str,
  isFuelStation: z.boolean().optional().transform((v) => v ?? true),
  alreadyConfigured: z.boolean().optional().transform((v) => v ?? false),
  configuredFuelCount: z.number().optional().transform((v) => v ?? 0),
});
